import { Stack } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { TreeToolbar } from "slot-definitions"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { Repeat } from "lib/@components/repeat"
import { If, Else } from "lib/@components/switch"
import { NoResults } from "lib/@components/no-results/no-results"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { hasAmendsContent } from "authoring/routes/project/amends/lib/amends-content"
import { getScheduleCommentaryUpdates } from "./controller/get-schedule-commentary-updates"
import { getCommentaryGroupsDraft } from "./controller/get-commentary-groups-draft"
import { ScheduleCommentaryCategorySection } from "./components/schedule-commentary-category-section"
import { ScheduleCommentaryOverviewSection } from "./components/schedule-commentary-overview-section"
import { ScheduleCommentaryAddSectionButton } from "./components/schedule-commentary-add-section-button"
import { assignCommentarySections, buildCommentaryPageSections } from "./lib/build-commentary-page-sections"
import { buildCommentaryOverview } from "./lib/build-commentary-overview"

export function ScheduleCommentaryPublishingPage() {
    const groups = getScheduleCommentaryUpdates.useResults({}, withInitialValue(IS_LOADING))
    const draft = getCommentaryGroupsDraft.useResults({}, withInitialValue(IS_LOADING))
    TreeToolbar.usePlug(<ScheduleCommentaryAddSectionButton />)

    if (groups === IS_LOADING || draft === IS_LOADING) return <LoadingScreen />

    const rows = assignCommentarySections(groups, draft.sections)
    const pageSections = buildCommentaryPageSections(rows, draft.sections)
    const overview = buildCommentaryOverview(rows)
    const showOverview = overview.length > 0 || hasAmendsContent(draft.overview.content)
    const excludedSection = buildExcludedSection(rows)
    const hasContent = pageSections.length > 0 || Boolean(excludedSection)

    return (
        <PageBreadcrumbContainer title="Schedule Commentary">
            <Bound commentarySections={draft.sections}>
                <If value={hasContent}>
                    <Stack spacing={4} sx={{ mt: 3 }}>
                        <If value={showOverview}>
                            <ScheduleCommentaryOverviewSection
                                title={draft.overview.title}
                                items={overview}
                                content={draft.overview.content}
                            />
                        </If>
                        <Repeat list={pageSections} item={<ScheduleCommentaryCategorySection />} keyFn={(s) => s.id} />
                        <If value={Boolean(excludedSection)}>
                            <ScheduleCommentaryCategorySection item={excludedSection} />
                        </If>
                    </Stack>
                    <Else>
                        <NoResults>No schedules found.</NoResults>
                    </Else>
                </If>
            </Bound>
        </PageBreadcrumbContainer>
    )
}

// Excluded schedules are pulled out of their section and listed together here instead, so a
// section only ever shows what's actually in the current publish round.
function buildExcludedSection(rows) {
    const excludedSchedules = rows.filter((row) => row.excludedFromCycle)
    if (!excludedSchedules.length) return null

    return {
        id: "excluded",
        custom: false,
        readOnly: true,
        title: "Excluded from this round",
        description:
            "These schedules won't be included the next time you publish. Include them again to add them back.",
        schedules: excludedSchedules,
    }
}
